// HFLedger/CategoryManager.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CategoryManager.css';

function CategoryManager({ user, language, SERVER_ROOT, settings }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  
  // 카테고리 기본 색상
  const defaultColors = [
    '#4CAF50', '#F44336', '#2196F3', '#FF9800', '#9C27B0', 
    '#009688', '#673AB7', '#FFEB3B', '#795548', '#607D8B'
  ];
  
  // 폼 데이터
  const [formData, setFormData] = useState({
    name: '',
    type: 'expense',
    color: defaultColors[0],
    icon: ''
  });
  
  // 데이터 불러오기
  useEffect(() => {
    if (!user) return;
    
    const fetchCategories = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${SERVER_ROOT}/api/hfl/categories/${user.uid}`);
        setCategories(response.data);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError(err.message || '카테고리 정보를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCategories();
  }, [user, SERVER_ROOT]);

  // 편집 시 폼 데이터 업데이트
  useEffect(() => {
    if (editingCategory) {
      setFormData({
        name: editingCategory.name,
        type: editingCategory.type,
        color: editingCategory.color,
        icon: editingCategory.icon || ''
      });
    } else {
      setFormData({
        name: '',
        type: 'expense',
        color: defaultColors[0],
        icon: ''
      });
    }
  }, [editingCategory]);

  // 입력 변경 핸들러
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 카테고리 리셋 핸들러 함수 추가
  const handleResetCategories = async () => {
    if (!window.confirm(language === 'KR' 
      ? '카테고리를 리셋하시겠습니까? 거래내역에 사용된 카테고리는 유지되고, 간소화된 기본 카테고리가 추가됩니다.' 
      : 'Do you want to reset categories? Categories used in transactions will be kept, and simplified default categories will be added.')) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.post(`${SERVER_ROOT}/api/hfl/categories/reset/${user.uid}`);
      
      // 새로고침
      const categoriesResponse = await axios.get(`${SERVER_ROOT}/api/hfl/categories/${user.uid}`);
      setCategories(categoriesResponse.data);
      
      alert(language === 'KR' 
        ? `카테고리가 리셋되었습니다. ${response.data.categories_kept}개의 카테고리가 유지되고, ${response.data.categories_added}개의 기본 카테고리가 추가되었습니다.` 
        : `Categories have been reset. ${response.data.categories_kept} categories kept and ${response.data.categories_added} default categories added.`);
      
    } catch (err) {
      console.error('Error resetting categories:', err);
      alert(language === 'KR' 
        ? '카테고리 리셋 중 오류가 발생했습니다.' 
        : 'Error resetting categories.');
    } finally {
      setLoading(false);
    }
  };

  // 폼 제출 핸들러
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 필수 필드 검증
    if (!formData.name.trim()) {
      setError(language === 'KR' 
        ? '카테고리 이름을 입력해주세요.' 
        : 'Please enter a category name.');
      return;
    }
    
    try {
      if (editingCategory) {
        // 카테고리 업데이트
        await axios.put(`${SERVER_ROOT}/api/hfl/categories/${editingCategory.category_id}`, {
          name: formData.name,
          color: formData.color,
          icon: formData.icon || null
        });
        
        // 목록 업데이트
        const updatedCategory = {
            ...editingCategory,
            name: formData.name,
            color: formData.color,
            icon: formData.icon || null
          };
          
          setCategories(prev => 
            prev.map(cat => cat.category_id === editingCategory.category_id ? updatedCategory : cat)
          );
        } else {
          // 새 카테고리 생성
          const response = await axios.post(`${SERVER_ROOT}/api/hfl/categories`, {
            user_id: user.uid,
            name: formData.name,
            type: formData.type,
            color: formData.color,
            icon: formData.icon || null
          });
          
          // 목록 업데이트
          const newCategory = response.data;
          setCategories(prev => [...prev, newCategory]);
        }
        
        // 폼 초기화
        setShowForm(false);
        setEditingCategory(null);
        setFormData({
          name: '',
          type: 'expense',
          color: defaultColors[0],
          icon: ''
        });
        setError(null);
        
      } catch (err) {
        console.error('Error saving category:', err);
        setError(err.message || (language === 'KR' 
          ? '카테고리를 저장하는 중 오류가 발생했습니다.' 
          : 'Error saving category.'));
      }
    };
  
    // 카테고리 삭제 핸들러
    const handleDeleteCategory = async (categoryId) => {
      // 기본 카테고리 삭제 방지
      const category = categories.find(cat => cat.category_id === categoryId);
      if (category && category.is_default) {
        alert(language === 'KR' 
          ? '기본 카테고리는 삭제할 수 없습니다.' 
          : 'Default categories cannot be deleted.');
        return;
      }
      
      if (!window.confirm(language === 'KR' 
        ? '이 카테고리를 삭제하시겠습니까? 해당 카테고리를 사용하는 거래가 있는 경우 삭제할 수 없습니다.' 
        : 'Are you sure you want to delete this category? It cannot be deleted if there are transactions using it.')) {
        return;
      }
      
      try {
        await axios.delete(`${SERVER_ROOT}/api/hfl/categories/${categoryId}`);
        
        // 목록에서 제거
        setCategories(prev => prev.filter(cat => cat.category_id !== categoryId));
        
      } catch (err) {
        console.error('Error deleting category:', err);
        
        // 거래에서 사용 중인 경우 처리
        if (err.response && err.response.status === 400) {
          alert(language === 'KR' 
            ? '이 카테고리를 사용하는 거래가 있어 삭제할 수 없습니다. 먼저 해당 거래의 카테고리를 변경해주세요.' 
            : 'Cannot delete category that is used in transactions. Please update those transactions first.');
        } else {
          alert(language === 'KR' ? '카테고리 삭제 중 오류가 발생했습니다.' : 'Error deleting category.');
        }
      }
    };
  
    // 카테고리 편집 핸들러
    const handleEditCategory = (category) => {
      setEditingCategory(category);
      setShowForm(true);
      setError(null);
    };
  
    // 기본 카테고리 생성 핸들러
    const handleCreateDefaultCategories = async () => {
      if (!window.confirm(language === 'KR' 
        ? '기본 카테고리를 생성하시겠습니까? 이미 등록된 카테고리가 있으면 생성되지 않습니다.' 
        : 'Do you want to create default categories? This will not affect existing categories.')) {
        return;
      }
      
      try {
        setLoading(true);
        const response = await axios.post(`${SERVER_ROOT}/api/hfl/categories/default/${user.uid}`);
        
        // 새로고침
        const categoriesResponse = await axios.get(`${SERVER_ROOT}/api/hfl/categories/${user.uid}`);
        setCategories(categoriesResponse.data);
        
        alert(language === 'KR' 
          ? `${response.data.categories}개의 기본 카테고리가 생성되었습니다.` 
          : `${response.data.categories} default categories created successfully.`);
        
      } catch (err) {
        console.error('Error creating default categories:', err);
        
        if (err.response && err.response.status === 400) {
          alert(language === 'KR' 
            ? '이미 카테고리가 등록되어 있습니다.' 
            : 'You already have categories.');
        } else {
          alert(language === 'KR' 
            ? '기본 카테고리 생성 중 오류가 발생했습니다.' 
            : 'Error creating default categories.');
        }
      } finally {
        setLoading(false);
      }
    };
  
    // 카테고리 필터링 및 정렬
    const filteredCategories = (type) => {
      return categories
        .filter(category => category.type === type)
        .sort((a, b) => {
          // 기본 카테고리 우선, 그 다음 이름순
          if (a.is_default !== b.is_default) return a.is_default ? -1 : 1;
          return a.name.localeCompare(b.name);
        });
    };
  
    // 아이콘 옵션
    const iconOptions = [
      { value: '', label: language === 'KR' ? '없음' : 'None' },
      { value: 'home', label: language === 'KR' ? '주거' : 'Home' },
      { value: 'restaurant', label: language === 'KR' ? '식비' : 'Food' },
      { value: 'directions_car', label: language === 'KR' ? '교통' : 'Transport' },
      { value: 'shopping_basket', label: language === 'KR' ? '쇼핑' : 'Shopping' },
      { value: 'local_hospital', label: language === 'KR' ? '의료' : 'Medical' },
      { value: 'school', label: language === 'KR' ? '교육' : 'Education' },
      { value: 'beach_access', label: language === 'KR' ? '여행' : 'Travel' },
      { value: 'fitness_center', label: language === 'KR' ? '운동' : 'Fitness' },
      { value: 'pets', label: language === 'KR' ? '반려동물' : 'Pets' },
      { value: 'work', label: language === 'KR' ? '급여' : 'Salary' },
      { value: 'card_giftcard', label: language === 'KR' ? '선물' : 'Gift' },
      { value: 'account_balance', label: language === 'KR' ? '투자' : 'Investment' },
      { value: 'receipt', label: language === 'KR' ? '공과금' : 'Bills' },
      { value: 'category', label: language === 'KR' ? '기타' : 'Others' }
    ];
  
    if (loading && categories.length === 0) {
      return (
        <div className="categories-loading">
          <div className="categories-spinner"></div>
          <p>{language === 'KR' ? '카테고리 로딩 중...' : 'Loading categories...'}</p>
        </div>
      );
    }
  
    return (
      <div className="category-manager-container">
        <div className="category-manager-header">
          <h3>{language === 'KR' ? '카테고리 관리' : 'Category Management'}</h3>
          <div className="category-actions">
            <button 
              className="add-category-button"
              onClick={() => {
                setEditingCategory(null);
                setShowForm(true);
                setError(null);
              }}
            >
              <i className="fas fa-plus"></i> 
              {language === 'KR' ? '새 카테고리' : 'New Category'}
            </button>
            
            {categories.length === 0 && (
              <button 
                className="default-categories-button"
                onClick={handleCreateDefaultCategories}
              >
                <i className="fas fa-list"></i> 
                {language === 'KR' ? '기본 카테고리 생성' : 'Create Default Categories'}
              </button>
            )}

            <button 
              className="reset-categories-button"
              onClick={handleResetCategories}
            >
              <i className="fas fa-redo"></i> 
              {language === 'KR' ? '카테고리 리셋' : 'Reset Categories'}
            </button>
          </div>
        </div>
        
        {/* 카테고리 추가/편집 폼 */}
        {showForm && (
          <div className="category-form-container">
            <h4>
              {editingCategory 
                ? (language === 'KR' ? '카테고리 수정' : 'Edit Category') 
                : (language === 'KR' ? '새 카테고리 추가' : 'Add Category')}
            </h4>
            
            <form onSubmit={handleSubmit} className="category-form">
              {/* 카테고리 타입 */}
              {!editingCategory && (
                <div className="form-group">
                  <label htmlFor="type">{language === 'KR' ? '유형' : 'Type'}</label>
                  <div className="type-buttons">
                    <button 
                      type="button"
                      className={`type-button ${formData.type === 'income' ? 'income active' : 'income'}`}
                      onClick={() => setFormData(prev => ({ ...prev, type: 'income' }))}
                    >
                      <i className="fas fa-arrow-down"></i> 
                      {language === 'KR' ? '수입' : 'Income'}
                    </button>
                    <button 
                      type="button"
                      className={`type-button ${formData.type === 'expense' ? 'expense active' : 'expense'}`}
                      onClick={() => setFormData(prev => ({ ...prev, type: 'expense' }))}
                    >
                      <i className="fas fa-arrow-up"></i> 
                      {language === 'KR' ? '지출' : 'Expense'}
                    </button>
                  </div>
                </div>
              )}
              
              {/* 카테고리 이름 */}
              <div className="form-group">
                <label htmlFor="name">
                  {language === 'KR' ? '이름' : 'Name'} <span className="required">*</span>
                </label>
                <input 
                  type="text" 
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={language === 'KR' ? '카테고리 이름 입력' : 'Enter category name'}
                  required
                />
              </div>
              
              {/* 카테고리 색상 */}
              <div className="form-group">
                <label htmlFor="color">{language === 'KR' ? '색상' : 'Color'}</label>
                <div className="color-picker">
                  <input 
                    type="color" 
                    id="color"
                    name="color"
                    value={formData.color}
                    onChange={handleInputChange}
                  />
                  <span className="color-preview" style={{ backgroundColor: formData.color }}></span>
                </div>
              </div>
              
              {/* 카테고리 아이콘 */}
              <div className="form-group">
                <label htmlFor="icon">{language === 'KR' ? '아이콘' : 'Icon'}</label>
                <select 
                  id="icon"
                  name="icon"
                  value={formData.icon}
                  onChange={handleInputChange}
                >
                  {iconOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {formData.icon && (
                  <div className="icon-preview">
                    <i className={`material-icons`}>{formData.icon}</i>
                  </div>
                )}
              </div>
              
              {/* 에러 메시지 */}
              {error && (
                <div className="form-error">
                  {error}
                </div>
              )}
              
              {/* 제출 버튼 */}
              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => {
                    setShowForm(false);
                    setError(null);
                  }}
                >
                  {language === 'KR' ? '취소' : 'Cancel'}
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                >
                  {editingCategory 
                    ? (language === 'KR' ? '수정' : 'Update') 
                    : (language === 'KR' ? '추가' : 'Add')}
                </button>
              </div>
            </form>
          </div>
        )}
        
        {/* 카테고리 목록 */}
        <div className="category-lists-container">
          {/* 수입 카테고리 */}
          <div className="category-list-section">
            <h4 className="income">{language === 'KR' ? '수입 카테고리' : 'Income Categories'}</h4>
            <div className="category-list">
              {filteredCategories('income').length === 0 ? (
                <p className="no-categories">
                  {language === 'KR' ? '수입 카테고리가 없습니다.' : 'No income categories.'}
                </p>
              ) : (
                filteredCategories('income').map(category => (
                  <div key={category.category_id} className="category-item">
                    <div 
                      className="category-color" 
                      style={{ backgroundColor: category.color }}
                    ></div>
                    <div className="category-info">
                      <span className="category-name">
                        {category.name}
                        {category.is_default && (
                          <span className="default-badge">
                            {language === 'KR' ? '기본' : 'Default'}
                          </span>
                        )}
                      </span>
                      {category.icon && (
                        <span className="category-icon">
                          <i className="material-icons">{category.icon}</i>
                        </span>
                      )}
                    </div>
                    <div className="category-actions">
                      <button 
                        className="edit-button"
                        onClick={() => handleEditCategory(category)}
                        disabled={loading}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button 
                        className="delete-button"
                        onClick={() => handleDeleteCategory(category.category_id)}
                        disabled={loading || category.is_default}
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          
          {/* 지출 카테고리 */}
          <div className="category-list-section">
            <h4 className="expense">{language === 'KR' ? '지출 카테고리' : 'Expense Categories'}</h4>
            <div className="category-list">
              {filteredCategories('expense').length === 0 ? (
                <p className="no-categories">
                  {language === 'KR' ? '지출 카테고리가 없습니다.' : 'No expense categories.'}
                </p>
              ) : (
                filteredCategories('expense').map(category => (
                  <div key={category.category_id} className="category-item">
                    <div 
                      className="category-color" 
                      style={{ backgroundColor: category.color }}
                    ></div>
                    <div className="category-info">
                      <span className="category-name">
                        {category.name}
                        {category.is_default && (
                          <span className="default-badge">
                            {language === 'KR' ? '기본' : 'Default'}
                          </span>
                        )}
                      </span>
                      {category.icon && (
                        <span className="category-icon">
                          <i className="material-icons">{category.icon}</i>
                        </span>
                      )}
                    </div>
                    <div className="category-actions">
                      <button 
                        className="edit-button"
                        onClick={() => handleEditCategory(category)}
                        disabled={loading}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button 
                        className="delete-button"
                        onClick={() => handleDeleteCategory(category.category_id)}
                        disabled={loading || category.is_default}
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        
        {/* 도움말 */}
        <div className="category-help">
          <p>
            <i className="fas fa-info-circle"></i>
            {language === 'KR' 
              ? '기본 카테고리는 삭제할 수 없습니다. 카테고리를 사용하는 거래가 있으면 해당 카테고리를 삭제할 수 없습니다.'
              : 'Default categories cannot be deleted. Categories used in transactions cannot be deleted.'}
          </p>
        </div>
      </div>
    );
  }
  
  export default CategoryManager;